<script lang="ts">
    import { onMount, setContext } from "svelte";
    import {
        FullscreenControl,
        LayerSpecification,
        LngLatLike,
        Map,
        NavigationControl,
    } from "maplibre-gl";
    import { key } from "./map";
    import type { MapHook } from "./MapHook";
    import { UrlHashHandler } from "../../../shared/UrlHashHandler";
    import { appManager } from "../../../AppManager";
    import type { ScenarioData } from "../../../api/projects/data/scenarios/ScenarioData";
    import type { BaseLayerLayerType } from "../../../lib/maplibre/baselayers/BaseLayerLayerType";

    // exports.
    export let hook: MapHook;
    export let center: LngLatLike;
    export let scenario1: ScenarioData;

    let map: Map;
    let urlHash = new UrlHashHandler("map");

    // get map context.
    setContext(key, {
        getMap: () => {
            return { map: map, hook: hook };
        },
    });
    onMount(async () => {
        let zoom: number = 14.56;
        const mapState = urlHash.getState();
        if (typeof mapState !== "undefined") {
            const parts = mapState.split("/");

            if (parts.length === 3) {
                center = [parseFloat(parts[1]), parseFloat(parts[2])];
                zoom = parseFloat(parts[0]);
            }
        }

        const styleResponse = await fetch(
            //"https://api.maptiler.com/maps/0d19e9c6-3baf-467f-abde-db0e28870f27/style.json?key=OZUCIh4RNx38vXF8gF4H"
            "https://api.maptiler.com/maps/basic/style.json?key=OZUCIh4RNx38vXF8gF4H",
        );
        const style = await styleResponse.json();

        // replace all the transportation layer sources.
        // TODO: remove the need to customize the oneways.
        // customize oneway layers, they are slightly different in the impact API.
        const sourceId = "impact-network";
        const source = {
            type: "vector",
            url: appManager.api.publish.buildBranchMvt(scenario1.branchId),
        };
        style.sources[sourceId] = source;
        for (var l = 0; l < style.layers.length; l++) {
            var layer = style.layers[l];

            if (layer) {
                var sourceLayer = layer["source-layer"];

                if (sourceLayer === "transportation") {
                    if (layer.id.includes("rail")) {
                        continue;
                    }
                    layer.source = sourceId;
                }

                if (layer.id === "road_oneway") {
                    layer.filter = [
                        "all",
                        ["any", ["==", "oneway", 1], ["==", "oneway", "yes"]],
                        [
                            "in",
                            "class",
                            "motorway",
                            "trunk",
                            "primary",
                            "secondary",
                            "tertiary",
                            "minor",
                            "service",
                        ],
                    ];
                }
            }
        }

        const bicycleAccess = [
            "all",
            ["!in", "class", "motorway"],
            ["!in", "bicycle", "use_sidepath", "no"],
        ];

        map = new Map({
            container: "maplibre-gl-container",
            style: style,
            center: center,
            zoom: zoom,
            hash: false,
            attributionControl: false,
        });

        const nav = new NavigationControl({
            visualizePitch: true,
        });
        map.addControl(nav, "top-right");

        map.addControl(new FullscreenControl());

        map.on("move", () => {
            const center = map.getCenter();
            urlHash.update(
                `${map.getZoom().toFixed(2)}/${center.lng.toFixed(
                    5,
                )}/${center.lat.toFixed(5)}`,
            );
        });

        map.on("load", () => {
            map.resize(); // one more resize, refresh on chrome broken.

            // get the lowest per type.
            const lowestByType: { [type: string]: string } = {};
            const style = map.getStyle();
            style.layers.forEach((layer) => {
                // get layer type.
                const layerType = getLayerType(layer);
                if (typeof layerType === "undefined") return; // no type.

                // see if type has been hit before.
                if (typeof lowestByType[layerType] !== "undefined") return; // already seen.
                lowestByType[layerType] = layer.id;
            });

            map.addLayer(
                {
                    id: "edges-selected-highway-construction-black",
                    type: "line",
                    source: "impact-network",
                    "source-layer": "transportation",
                    maxzoom: 24,
                    paint: {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        /* @ts-ignore */
                        "line-color": "rgba(12, 12, 12, 1)",
                        "line-width": {
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            base: 1.2,
                            stops: [
                                [15, 2],
                                [20, 18],
                            ],
                        },
                    },
                    filter: [
                        "any",
                        [
                            "all",
                            ["==", "$type", "LineString"],
                            ["in", "highway", "construction"],
                        ],
                    ],
                },
                lowestByType["symbol"],
            );

            map.addLayer(
                {
                    id: "edges-selected-highway-construction",
                    type: "line",
                    source: "impact-network",
                    "source-layer": "transportation",
                    paint: {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        /* @ts-ignore */
                        "line-color": "rgba(242, 255, 0, 1)",
                        "line-width": {
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            base: 1.2,
                            stops: [
                                [6.5, 0],
                                [8, 0.4],
                                [20, 10],
                            ],
                        },
                        "line-dasharray": [2.5, 2],
                    },
                    filter: [
                        "any",
                        [
                            "all",
                            ["==", "$type", "LineString"],
                            ["in", "highway", "construction"],
                        ],
                    ],
                },
                lowestByType["symbol"],
            );

            map.addLayer(
                {
                    id: "detour",
                    type: "line",
                    source: "impact-network",
                    "source-layer": "transportation",
                    paint: {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        /* @ts-ignore */
                        "line-color": "#0000ff",
                        "line-width": {
                            base: 1.2,
                            stops: [
                                [6.5, 0],
                                [8, 0.4],
                                [20, 10],
                            ],
                        },
                    },
                    filter: [
                        "any",
                        [
                            "all",
                            ["==", "$type", "LineString"],
                            ["in", "anyways:detour", "yes"],
                        ],
                    ],
                },
                lowestByType["symbol"],
            );

            map.addLayer(
                {
                    id: "cyclestreets",
                    type: "line",
                    source: "impact-network",
                    "source-layer": "transportation",
                    paint: {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        /* @ts-ignore */
                        "line-color": "#6c4e4c",
                        "line-opacity": 0.6,
                        "line-width": {
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            base: 1.2,
                            stops: [
                                [6.5, 0],
                                [8, 0.4],
                                [20, 10],
                            ],
                        },
                    },
                    filter: [
                        "any",
                        [
                            "all",
                            ["==", "$type", "LineString"],
                            ["in", "cyclestreet", "yes"],
                        ],
                    ],
                },
                lowestByType["symbol"],
            );

            map.addLayer(
                {
                    id: "anyways:zones",
                    type: "fill",
                    source: "impact-network",
                    "source-layer": "transportation",
                    filter: ["all", ["has", "anyways:zone"]],
                    paint: {
                        "fill-color": [
                            "case",
                            ["has", "anyways:color"],
                            ["get", "anyways:color"],
                            "rgba(255, 0, 0, 1)",
                        ],
                        "fill-opacity": 0.5,
                    },
                },
                lowestByType["road"],
            );
            map.addLayer(
                {
                    id: "anyways:roadcolor",
                    type: "line",
                    source: sourceId,
                    "source-layer": "transportation",
                    paint: {
                        "line-color": [
                            "case",
                            ["has", "anyways:color"],
                            ["get", "anyways:color"],
                            "rgba(255, 0, 0, 1)",
                        ],
                        "line-width": {
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            /* @ts-ignore */
                            base: 1.2,
                            stops: [
                                [6.5, 0],
                                [8, 0.4],
                                [20, 10],
                            ],
                        },
                    },
                    filter: [
                        "any",
                        [
                            "all",
                            ["==", "$type", "LineString"],
                            ["has", "anyways:color"],
                        ],
                    ],
                },
                lowestByType["symbol"],
            );
        });

        hook.resize = () => {
            map.resize();
        };
        hook.flyTo = (center) => {
            map.flyTo({
                center: [center.lng, center.lat],
                minZoom: 15,
            });
        };
        hook.on = (name, handler) => {
            map.on(name, (e) => handler(e));
        };
    });

    function getLayerType(layer: LayerSpecification): BaseLayerLayerType {
        if (layer && layer["source-layer"] === "transportation") {
            return "road";
        }

        if (layer && layer["source-layer"] === "transportation_name") {
            return "label";
        }

        if (layer && layer.type == "symbol" && layer.id !== "housenumber") {
            return "symbol";
        }

        if (layer.id == "background") {
            return "background";
        }

        return undefined;
    }
</script>

<div id="maplibre-gl-container" class="h-100 map">
    {#if map}
        <slot />
    {/if}
</div>

<style>
</style>
